import { queryOptions, useQuery } from '@tanstack/react-query';
import { getToken } from '@shared/auth';
import { getValidated, AppError } from '@shared/api';
import { MeResponseSchema } from './schemas';
import { mapMeToSession } from './mappers';
import type { SessionUser } from '../model/types';

/**
 * Session query: fetches the current user from GET /api/v1/auth/me. A missing token throws
 * immediately (no network call); the router guard + 401 interceptor both funnel to /login.
 * staleTime: 5 min — cached across navigations, re-fetched on window focus when stale.
 *
 * Throws the shared AppError('auth') shape (not a plain Error) — the login route's beforeLoad
 * runs this via ensureQueryData().catch(() => null) to check for an existing session, but
 * QueryCache's global onError still fires regardless of that .catch(); an unrecognized error
 * kind there surfaces as a generic "unexpected error" toast on every /login load.
 */
export const sessionQueryOptions = queryOptions({
  queryKey: ['session'],
  queryFn: async (): Promise<SessionUser> => {
    if (!getToken()) {
      throw new AppError({ kind: 'auth', messageForUser: 'Please sign in to continue.' });
    }
    const res = await getValidated('api/v1/auth/me', MeResponseSchema);
    return mapMeToSession(res.data);
  },
  staleTime: 5 * 60_000,
  retry: false,
});

export function useSession() {
  return useQuery(sessionQueryOptions);
}
